import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLocale } from '../contexts/LocaleContext'
import { siteConfig, getCitySlugWithPrefix } from '../config/site'
import { updateSEO } from '../utils/seo'
import { filterActiveCities } from '../utils/activeCities'
import citiesData from '../data/cities.json'
import './Home.css'

const Home = () => {
  const [cities, setCities] = useState([])
  const [filteredCities, setFilteredCities] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const navigate = useNavigate()
  const { t, translations } = useLocale()

  useEffect(() => {
    // Load cities data and filter by active cities
    const activeCities = filterActiveCities(citiesData)
    setCities(activeCities)
    setFilteredCities(activeCities)
  }, [])

  useEffect(() => {
    // Update SEO meta tags
    updateSEO({
      title: t('seo.homeTitle'),
      description: t('seo.homeDescription'),
      keywords: t('seo.homeKeywords'),
      url: siteConfig.baseUrl
    })
  }, [t])

  useEffect(() => {
    // Filter cities based on search query
    if (!searchQuery.trim()) {
      setFilteredCities(cities)
      return
    }

    const filtered = cities.filter((city) =>
      city.City.toLowerCase().includes(searchQuery.toLowerCase()) ||
      city.Slug.toLowerCase().includes(searchQuery.toLowerCase())
    )
    setFilteredCities(filtered)
  }, [searchQuery, cities])

  const handleCityClick = (citySlug) => {
    const fullSlug = getCitySlugWithPrefix(citySlug)
    navigate(`/${fullSlug}`)
  }

  // Sort cities by population (most popular first)
  const sortedCities = [...filteredCities].sort((a, b) => b.Population - a.Population)
  const popularCities = sortedCities.slice(0, 6)

  return (
    <div className="home-container">
      <header className="home-header">
        <h1 className="home-title">{t('home.title')}</h1>
        <p className="home-subtitle">{t('home.subtitle')}</p>
      </header>

      <section className="choose-city-section">
        <h2 className="choose-city-title">{t('home.chooseCity')}</h2>
        <p className="choose-city-description">{t('home.chooseCityDescription')}</p>
        
        <div className="search-section">
          <input
            type="text"
            className="city-search-input"
            placeholder={t('home.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </section>

      {searchQuery && (
        <section className="cities-section">
          <h2 className="section-title">{t('home.allCities')}</h2>
          <div className="cities-grid">
            {sortedCities.map((city) => (
              <div
                key={city.Slug}
                className="city-card"
                onClick={() => handleCityClick(city.Slug)}
              >
                <h3 className="city-name">{city.City}</h3>
                <p className="city-population">
                  {t('common.population')}: {city.Population.toLocaleString('de-DE')}
                </p>
              </div>
            ))}
          </div>
          {sortedCities.length === 0 && (
            <p className="no-results">Keine Städte gefunden</p>
          )}
        </section>
      )}

      {!searchQuery && (
        <>
          {/* SEO Content Section */}
          <section className="seo-content-section">
            <div className="seo-content">
              <p className="seo-intro">{t('home.seoContent.intro')}</p>
              
              <div className="seo-features">
                <div className="seo-feature">
                  <h2 className="seo-feature-title">{t('home.seoContent.whyChoose')}</h2>
                  <p className="seo-feature-text">{t('home.seoContent.whyChooseText')}</p>
                </div>
                
                <div className="seo-feature">
                  <h2 className="seo-feature-title">{t('home.seoContent.howItWorks')}</h2>
                  <p className="seo-feature-text">{t('home.seoContent.howItWorksText')}</p>
                </div>
                
                <div className="seo-feature">
                  <h2 className="seo-feature-title">{t('home.seoContent.safety')}</h2>
                  <p className="seo-feature-text">{t('home.seoContent.safetyText')}</p>
                </div>
              </div>
            </div>
          </section>

          <section className="cities-section">
            <h2 className="section-title">{t('home.popularCities')}</h2>
            <div className="cities-grid">
              {popularCities.map((city) => (
                <div
                  key={city.Slug}
                  className="city-card"
                  onClick={() => handleCityClick(city.Slug)}
                >
                  <h3 className="city-name">{city.City}</h3>
                  <p className="city-population">
                    {t('common.population')}: {city.Population.toLocaleString('de-DE')}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="cities-section">
            <h2 className="section-title">{t('home.allCities')}</h2>
            <div className="cities-grid">
              {cities.map((city) => (
                <div
                  key={city.Slug}
                  className="city-card"
                  onClick={() => handleCityClick(city.Slug)}
                >
                  <h3 className="city-name">{city.City}</h3>
                  <p className="city-population">
                    {t('common.population')}: {city.Population.toLocaleString('de-DE')}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="faq-section">
            <div className="faq-container">
              <h2 className="faq-title">{t('home.faq.title')}</h2>
              <p className="faq-subtitle">{t('home.faq.subtitle')}</p>
              
              <div className="faq-list">
                {translations?.home?.faq?.items?.map((faq, index) => (
                  <div key={index} className="faq-item">
                    <h3 className="faq-question">{faq.question}</h3>
                    <p className="faq-answer">{faq.answer}</p>
                  </div>
                )) || []}
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  )
}

export default Home
